/* eslint-disable react/jsx-no-duplicate-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { InputProps as MaterialInputProps } from '@material-ui/core/Input';
import { StandardTextFieldProps } from '@material-ui/core/TextField';
import { TextFieldAtom, TextFieldAtomProps } from '../atoms/TextFieldAtom/TextFieldAtom';
import { SystemIconButton } from '@utmost.co/utmost-design-system';
import styled from 'styled-components';
import { MatchHintSlot } from './MatchHintSlot';

interface Props<K> {
  name: string;
  required?: boolean;
  helperText?: string;
  infoText?: string;
  placeholder?: string;
  label?: string;
  type?: React.InputHTMLAttributes<unknown>['type'];
  multiline?: boolean;
  initialValue?: string;
  fullWidth?: boolean;
  mask?: TextFieldAtomProps['mask'];
  maskProps?: TextFieldAtomProps['maskProps'];
  inputProps?: StandardTextFieldProps['inputProps'];
  InputProps?: Partial<MaterialInputProps>;
  mapInputValue?: (value: K) => string;
  mapOutputValue?: (val?: string) => K;
  onChange?: (value: K) => void;
  className?: string;
  disabled?: boolean;
  parseNumbers?: boolean;
  disablePermissionDebugger?: boolean;
  clearable?: boolean;
}

export const TextField = <K extends any = string>({
  name,
  required,
  helperText,
  infoText,
  label,
  placeholder,
  type,
  initialValue,
  multiline,
  fullWidth,
  mask,
  maskProps,
  inputProps,
  parseNumbers,
  InputProps,
  mapInputValue,
  mapOutputValue,
  className,
  disabled,
  disablePermissionDebugger,
  onChange,
  clearable
}: Props<K>) => {
  const [input, meta, helpers] = useField<K>(name || 'INVALID');
  const [isFocused, setFocused] = useState(false);

  const formikContext = useFormikContext();
  const [isInitialValueSet, setIsInitialValueSet] = useState(false);
  const valueSetter = useMemo(() => onChange ?? helpers.setValue, [onChange, helpers.setValue]);
  // Set initial value if its provided
  useEffect(() => {
    const canSet = !isInitialValueSet && !!initialValue;
    if (canSet) {
      if (parseNumbers) {
        const parsedInitialValue = type === 'number' ? parseFloat(initialValue || '0') : initialValue;
        valueSetter(mapOutputValue ? mapOutputValue(initialValue) : (parsedInitialValue as K));
      } else {
        valueSetter(mapOutputValue ? mapOutputValue(initialValue) : (initialValue as K));
      }
      setIsInitialValueSet(true);
    }
  }, [parseNumbers, mapOutputValue, isInitialValueSet, initialValue, type, valueSetter]);

  // Update the formik value
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = e.currentTarget.value;
      valueSetter(mapOutputValue ? mapOutputValue(val) : (val as K));
    },
    [valueSetter, mapOutputValue]
  );

  const handleBlur = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(false);
      const val = e.currentTarget.value;
      if (parseNumbers) {
        const parsedInitialValue =
          type === 'number' ? (Number.isNaN(parseFloat(val)) ? undefined : parseFloat(val)) : val;
        valueSetter(mapOutputValue ? mapOutputValue(val) : (parsedInitialValue as K));
      }
      helpers.setTouched(true, false);
    },
    [valueSetter, mapOutputValue, parseNumbers, type, helpers]
  );

  // Reset touched to false on (re-)focus so a subsequent blur can be detected as a fresh
  // false -> true transition by consumers watching Formik's touched state (e.g. blur-triggered
  // side effects), rather than staying permanently true after the first blur.
  const handleFocus = useCallback(() => {
    setFocused(true);
    helpers.setTouched(false, false);
  }, [helpers]);

  const hasSubmitted = formikContext.submitCount > 0;

  if (clearable) {
    InputProps = {
      ...InputProps,
      endAdornment: isFocused && input.value && (
        <StyledSystemIconButton
          aria-label="Clear"
          onClick={() => valueSetter(mapOutputValue ? mapOutputValue(undefined) : (undefined as K))}
          onMouseDown={(e) => e.preventDefault()}
          iconName="close"
          size="x-small"
        />
      )
    };
  }

  return (
    <MatchHintSlot name={name}>
      <TextFieldAtom
        name={name}
        label={label}
        mask={mask}
        maskProps={maskProps}
        placeholder={placeholder}
        key={name}
        fullWidth={fullWidth}
        type={parseNumbers ? 'text' : type || 'text'}
        helperText={hasSubmitted && meta.error ? meta.error : helperText}
        infoText={infoText}
        value={mapInputValue ? mapInputValue(input.value) : input.value}
        required={required}
        error={hasSubmitted && !!meta.error}
        inputProps={inputProps}
        InputProps={InputProps}
        multiline={multiline}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className={className}
        disabled={disabled}
        disablePermissionDebugger={disablePermissionDebugger}
      />
    </MatchHintSlot>
  );
};

const StyledSystemIconButton = styled(SystemIconButton)`
  && {
    margin-right: 4px;
    padding: 0;
  }
`;
